import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail, useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  venue: "", contact_email: "", area: "", city: "", date: "2026-09-28",
  start: "18:00", end: "02:00", rate: 20, type: "Event", currency: "GBP", spots: 2, dress: "",
};

export default function VenuePost() {
  const { user } = useAuth();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [joinBusy, setJoinBusy] = useState(false);

  const isMember = !!(user && (user.founding_pass || user.membership_active));

  const joinMembership = async () => {
    setJoinBusy(true);
    try {
      const res = await fetch(`${API}/payments/membership`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.message);
    } finally {
      setJoinBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/shifts/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, rate: Number(form.rate), spots: Number(form.spots) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setDone(true);
      toast.success("Shift queued — our team reviews it within 24 hours.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "h-12 w-full rounded-md border border-white/10 bg-[#07090e] px-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none";

  return (
    <section id="post-shift" data-testid="venue-post-section" className="py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="font-mono-tag text-[11px] text-amber-500">For venues & businesses</p>
          <h2 className="font-display mt-4 text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl">
            Queue your shifts before launch
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
            Post your opening-week shifts now. We review every submission, then it goes live
            on the board — and verified, SIA-licensed guards claim it the moment the app launches
            on Sep 28.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-slate-400">
            <li className="flex gap-3"><span className="text-amber-500">—</span> £5.99/month membership — post unlimited shifts, cancel anytime</li>
            <li className="flex gap-3"><span className="text-amber-500">—</span> Clean shifts can auto-publish; anything unusual gets a human review</li>
            <li className="flex gap-3"><span className="text-amber-500">—</span> No-show guard = banned + you report problems straight to our review team</li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-7"
        >
          {!user ? (
            <div data-testid="venue-signin-prompt" className="rounded-2xl border border-white/10 bg-[#0f141c] p-10 text-center">
              <p className="font-display text-xl font-bold uppercase text-slate-50">Sign in to post shifts</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Creating a venue account is free — the £5.99/month membership is what unlocks posting.
                Use the <span className="text-amber-500">Sign In</span> button at the top of the page.
              </p>
            </div>
          ) : !isMember ? (
            <div data-testid="venue-membership-cta" className="glow-amber rounded-2xl border border-amber-500/30 bg-[#0f141c] p-10 text-center">
              <p className="font-mono-tag text-[10px] text-amber-500">Membership required to post</p>
              <p className="font-display mt-3 text-3xl font-black text-slate-50">£5.99<span className="text-lg text-slate-500">/month</span></p>
              <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                One membership, both sides of the board. Post unlimited shifts, reach verified SIA-licensed
                guards — and every no-show costs the guard their account, not your night.
              </p>
              <button
                data-testid="venue-join-btn"
                onClick={joinMembership}
                disabled={joinBusy}
                className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-md bg-amber-500 font-display text-base font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 disabled:opacity-50"
              >
                {joinBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Join & post your first shift
              </button>
              <p className="mt-3 font-mono-tag text-[8px] text-slate-600">Cancel anytime · secure checkout via Stripe</p>
            </div>
          ) : done ? (
            <div data-testid="venue-post-success" className="glow-amber flex flex-col items-center gap-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-400" />
              <p className="font-display text-2xl font-bold uppercase text-slate-50">Shift in the queue</p>
              <p className="max-w-sm text-sm text-slate-400">
                Confirmation is on its way to your inbox. Once approved, your shift appears on the live board.
              </p>
              <button
                data-testid="venue-post-another-btn"
                onClick={() => { setDone(false); setForm(empty); }}
                className="font-mono-tag mt-2 text-[10px] text-amber-500 underline underline-offset-4 hover:text-amber-400"
              >
                Post another shift
              </button>
            </div>
          ) : (
            <form data-testid="venue-post-form" onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#0f141c] p-8">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500">
                  <Building2 className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h3 className="font-display text-xl font-bold uppercase text-slate-50">Post a shift</h3>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input data-testid="vp-venue" required value={form.venue} onChange={set("venue")} placeholder="Venue name" className={inputCls} />
                <input data-testid="vp-email" required type="email" value={form.contact_email} onChange={set("contact_email")} placeholder="Contact email" className={inputCls} />
                <input data-testid="vp-area" required value={form.area} onChange={set("area")} placeholder="Area (e.g. Shoreditch)" className={inputCls} />
                <input data-testid="vp-city" required value={form.city} onChange={set("city")} placeholder="City" className={inputCls} />
                <input data-testid="vp-date" required type="date" min="2026-09-28" value={form.date} onChange={set("date")} className={inputCls} />
                <div className="flex gap-3">
                  <input data-testid="vp-start" required type="time" value={form.start} onChange={set("start")} className={inputCls} />
                  <input data-testid="vp-end" required type="time" value={form.end} onChange={set("end")} className={inputCls} />
                </div>
                <input data-testid="vp-rate" required type="number" min="10" max="200" value={form.rate} onChange={set("rate")} placeholder="Rate £/hr" className={inputCls} />
                <input data-testid="vp-spots" required type="number" min="1" max="50" value={form.spots} onChange={set("spots")} placeholder="Spots" className={inputCls} />
                <select data-testid="vp-type" value={form.type} onChange={set("type")} className={inputCls}>
                  {["Event", "Door Supervisor", "Security Guard", "Supervisor"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <div className="flex h-12 items-center rounded-md border border-white/10 bg-[#07090e] px-4 text-sm text-slate-400">
                  £ GBP — United Kingdom only
                </div>
                <input data-testid="vp-dress" value={form.dress} onChange={set("dress")} placeholder="Dress code (optional)" className={inputCls} />
              </div>
              <button
                data-testid="vp-submit"
                type="submit"
                disabled={busy}
                className="mt-6 flex h-13 w-full items-center justify-center gap-2 rounded-md bg-amber-500 py-3.5 font-display text-base font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Queue shift for review
              </button>
              <p className="mt-4 text-center font-mono-tag text-[8px] text-slate-600">Free to post · reviewed within 24h · goes live at launch</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
